
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * TradingAccountRequest struct
 */
class TradingAccountRequest {
  /**
   * Initialize struct
   * @param {!string=} id
   * @param {!number=} tradingAccountId
   * @constructor
   */
  constructor (id = '', tradingAccountId = new UInt64(0, 0)) {
    this.id = id
    this.tradingAccountId = tradingAccountId
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TradingAccountRequest}
   * @param {!TradingAccountRequest} other Other struct
   * @returns {!TradingAccountRequest} This struct
   */
  copy (other) {
    if (other.id != null) {
      this.id = other.id
    } else {
      this.id = null
    }
    if (other.tradingAccountId != null) {
      this.tradingAccountId = UInt64.fromNumber(other.tradingAccountId)
    } else {
      this.tradingAccountId = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TradingAccountRequest}
   * @returns {!TradingAccountRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TradingAccountRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TradingAccountRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TradingAccountRequest}
   * @param {!TradingAccountRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TradingAccountRequest)) {
      throw new TypeError('Instance of TradingAccountRequest is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TradingAccountRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      id: ((this.id != null) ? this.id : null),
      tradingAccountId: ((this.tradingAccountId != null) ? this.tradingAccountId.toNumber() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TradingAccountRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TradingAccountRequest} other Object value
   * @returns {!TradingAccountRequest} Created struct
   */
  static fromObject (other) {
    return new TradingAccountRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TradingAccountRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TradingAccountRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TradingAccountRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10011
  }
}

export { TradingAccountRequest };

/**
 * Fast Binary Encoding TradingAccountRequest field model
 */
class FieldModelTradingAccountRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.id = new fbe.FieldModelString(buffer, 4 + 4)
    this.tradingAccountId = new fbe.FieldModelUInt64(buffer, this.id.fbeOffset + this.id.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelTradingAccountRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTradingAccountRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.id.fbeSize + this.tradingAccountId.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTradingAccountRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.id.fbeExtra + this.tradingAccountId.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTradingAccountRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTradingAccountRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTradingAccountRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10011
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTradingAccountRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTradingAccountRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.id.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.tradingAccountId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.tradingAccountId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.tradingAccountId.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTradingAccountRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTradingAccountRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTradingAccountRequest}
   * @param {!TradingAccountRequest} fbeValue Default value, defaults is new TradingAccountRequest()
   * @returns {!TradingAccountRequest} TradingAccountRequest value
   */
  get (fbeValue = new TradingAccountRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTradingAccountRequest}
   * @param {!TradingAccountRequest} fbeValue TradingAccountRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) <= fbeStructSize) {
      fbeValue.id = this.id.get()
    } else {
      fbeValue.id = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.tradingAccountId.fbeSize) <= fbeStructSize) {
      fbeValue.tradingAccountId = this.tradingAccountId.get()
    } else {
      fbeValue.tradingAccountId = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.tradingAccountId.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTradingAccountRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTradingAccountRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTradingAccountRequest}
   * @param {!TradingAccountRequest} fbeValue TradingAccountRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTradingAccountRequest}
   * @param {TradingAccountRequest} fbeValue TradingAccountRequest value
   */
  setFields (fbeValue) {
    this.id.set(fbeValue.id)
    this.tradingAccountId.set(fbeValue.tradingAccountId)
  }
}

export { FieldModelTradingAccountRequest };

/**
 * Fast Binary Encoding TradingAccountRequest model
 */
class TradingAccountRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTradingAccountRequest(this.buffer, 4)
  }

  /**
   * Get the TradingAccountRequest model
   * @this {!TradingAccountRequestModel}
   * @returns {!FieldModelTradingAccountRequest} model TradingAccountRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TradingAccountRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TradingAccountRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TradingAccountRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TradingAccountRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTradingAccountRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TradingAccountRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TradingAccountRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TradingAccountRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TradingAccountRequestModel}
   * @param {!TradingAccountRequest} value TradingAccountRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TradingAccountRequestModel}
   * @param {!TradingAccountRequest} value TradingAccountRequest value, defaults is new TradingAccountRequest()
   * @return {!object} Deserialized TradingAccountRequest value and its size
   */
  deserialize (value = new TradingAccountRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TradingAccountRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TradingAccountRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TradingAccountRequestModel}
   * @param {!number} prev Previous TradingAccountRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TradingAccountRequestModel };
